import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { belotContractCpLabelFromEngine } from '../../../game/belotArmenianCards';

export function useBelotPreGameOrchestrator({
  engineState,
  humanSeat,
  applyRef,
  stateRef,
  contractBadgeAnchorRef,
  play,
  largeTrumpDisplay,
  auctionRevealMs,
  declareVoteSeconds,
}) {
  const [auctionRevealFx, setAuctionRevealFx] = useState(null);
  const [declareVoteReadyBySeat, setDeclareVoteReadyBySeat] = useState(() => ({}));
  const [declareVoteDeadlineAt, setDeclareVoteDeadlineAt] = useState(null);
  const [declareVoteNowMs, setDeclareVoteNowMs] = useState(Date.now());
  const [declareVoteBlockedUntilMs, setDeclareVoteBlockedUntilMs] = useState(0);
  const [declareVoteModalBlocked, setDeclareVoteModalBlocked] = useState(false);
  const [auctionRevealFallbackBanner, setAuctionRevealFallbackBanner] = useState('');

  const declareVoteBotTimersRef = useRef([]);
  const declareVoteIntervalRef = useRef(null);
  const declareVoteAutoSubmittedRef = useRef(false);
  const auctionRevealTimerRef = useRef(null);
  const declareVoteUnblockTimerRef = useRef(null);
  const auctionRevealBannerTimerRef = useRef(null);
  const auctionRevealWatchdogRef = useRef(null);
  const prevPhaseRef = useRef(engineState.phase);
  const lastAuctionRevealKeyRef = useRef('');

  const clearDeclareVoteTimers = useCallback(() => {
    declareVoteBotTimersRef.current.forEach((id) => clearTimeout(id));
    declareVoteBotTimersRef.current = [];
    if (declareVoteIntervalRef.current) {
      clearInterval(declareVoteIntervalRef.current);
      declareVoteIntervalRef.current = null;
    }
  }, []);

  const handleAuctionEndedReveal = useCallback((payload) => {
    const bid = payload?.bid && typeof payload.bid === 'object' ? payload.bid : null;
    const cpLabel = belotContractCpLabelFromEngine({
      contractTargetPoints: payload?.contractTargetPoints,
      kapot: bid?.kapot,
      tens: bid?.tens,
    });
    const trump = payload?.trump ?? payload?.bid?.trump ?? null;
    const mult = Number(payload?.contraMultiplier || 1);
    const now = Date.now();
    const revealKey = `${payload?.bidAuthorSeat ?? 'x'}:${cpLabel}:${String(trump)}:${Number.isFinite(mult) ? mult : 1}:${engineState.roundIndex}`;
    if (lastAuctionRevealKeyRef.current === revealKey) return;
    lastAuctionRevealKeyRef.current = revealKey;
    const revealLabel = `Контракт: ${cpLabel} CP ${largeTrumpDisplay(trump).line1}${Number.isFinite(mult) && mult > 1 ? ` ×${mult}` : ''}`;
    const anchorRect = contractBadgeAnchorRef?.current?.getBoundingClientRect?.();
    const viewportCx = typeof window !== 'undefined' ? window.innerWidth / 2 : 0;
    const viewportCy = typeof window !== 'undefined' ? window.innerHeight / 2 : 0;
    const anchorCx = anchorRect ? anchorRect.left + anchorRect.width / 2 : viewportCx;
    const anchorCy = anchorRect ? anchorRect.top + anchorRect.height / 2 : viewportCy;
    const fromX = anchorCx - viewportCx;
    const fromY = anchorCy - viewportCy;
    setAuctionRevealFx({
      token: `${now}-${Math.random().toString(36).slice(2, 7)}`,
      points: cpLabel,
      trump,
      multiplier: Number.isFinite(mult) && mult > 1 ? mult : 1,
      fromX,
      fromY,
    });
    setAuctionRevealFallbackBanner(revealLabel);
    setDeclareVoteBlockedUntilMs(now + auctionRevealMs + 180);
    setDeclareVoteModalBlocked(true);
    play('belot-contra-hit', 0.68);
    if (auctionRevealTimerRef.current) clearTimeout(auctionRevealTimerRef.current);
    if (declareVoteUnblockTimerRef.current) clearTimeout(declareVoteUnblockTimerRef.current);
    if (auctionRevealBannerTimerRef.current) clearTimeout(auctionRevealBannerTimerRef.current);
    auctionRevealTimerRef.current = setTimeout(() => {
      setAuctionRevealFx(null);
      auctionRevealTimerRef.current = null;
    }, auctionRevealMs);
    declareVoteUnblockTimerRef.current = setTimeout(() => {
      setDeclareVoteModalBlocked(false);
      declareVoteUnblockTimerRef.current = null;
    }, auctionRevealMs + 180);
    auctionRevealBannerTimerRef.current = setTimeout(() => {
      setAuctionRevealFallbackBanner('');
      auctionRevealBannerTimerRef.current = null;
    }, auctionRevealMs + 900);
  }, [auctionRevealMs, engineState.roundIndex, largeTrumpDisplay, play, contractBadgeAnchorRef]);

  useEffect(() => {
    return () => {
      if (auctionRevealTimerRef.current) clearTimeout(auctionRevealTimerRef.current);
      if (declareVoteUnblockTimerRef.current) clearTimeout(declareVoteUnblockTimerRef.current);
      if (auctionRevealBannerTimerRef.current) clearTimeout(auctionRevealBannerTimerRef.current);
      if (auctionRevealWatchdogRef.current) clearTimeout(auctionRevealWatchdogRef.current);
      clearDeclareVoteTimers();
    };
  }, [clearDeclareVoteTimers]);

  useEffect(() => {
    const prevPhase = prevPhaseRef.current;
    const nextPhase = engineState.phase;
    prevPhaseRef.current = nextPhase;
    if (auctionRevealWatchdogRef.current) {
      clearTimeout(auctionRevealWatchdogRef.current);
      auctionRevealWatchdogRef.current = null;
    }
    const transitionedToDeclareWithContract =
      prevPhase === 'auction' &&
      nextPhase === 'declare' &&
      Boolean(engineState.contract);
    if (!transitionedToDeclareWithContract) return;
    auctionRevealWatchdogRef.current = setTimeout(() => {
      auctionRevealWatchdogRef.current = null;
      if (stateRef.current?.phase !== 'declare') return;
      if (auctionRevealFx) return;
      handleAuctionEndedReveal({
        bidAuthorSeat: engineState.contract?.bidAuthorSeat ?? null,
        contractTargetPoints: engineState.contract?.contractTargetPoints ?? null,
        trump: engineState.contract?.trump ?? null,
        contraMultiplier: engineState.contract?.contraMultiplier ?? 1,
        bid: engineState.auction?.highestBid ?? null,
      });
    }, 0);
  }, [
    engineState.phase,
    engineState.contract,
    engineState.auction,
    auctionRevealFx,
    handleAuctionEndedReveal,
    stateRef,
  ]);

  const inDeclare = engineState.phase === 'declare';
  useEffect(() => {
    if (!inDeclare) {
      clearDeclareVoteTimers();
      declareVoteAutoSubmittedRef.current = false;
      setDeclareVoteReadyBySeat({});
      setDeclareVoteDeadlineAt(null);
      return;
    }
    clearDeclareVoteTimers();
    declareVoteAutoSubmittedRef.current = false;
    const startAt = Date.now();
    setDeclareVoteNowMs(startAt);
    setDeclareVoteDeadlineAt(startAt + declareVoteSeconds * 1000);
    setDeclareVoteReadyBySeat({ 0: false, 1: false, 2: false, 3: false });
    declareVoteIntervalRef.current = setInterval(() => {
      setDeclareVoteNowMs(Date.now());
    }, 200);
    for (let seat = 1; seat <= 3; seat += 1) {
      const delay = 1200 + Math.floor(Math.random() * 4500);
      const t = setTimeout(() => {
        setDeclareVoteReadyBySeat((prev) => ({ ...prev, [seat]: true }));
      }, delay);
      declareVoteBotTimersRef.current.push(t);
    }
    const timeoutId = setTimeout(() => {
      if (declareVoteAutoSubmittedRef.current) return;
      declareVoteAutoSubmittedRef.current = true;
      setDeclareVoteReadyBySeat({ 0: true, 1: true, 2: true, 3: true });
      if (stateRef.current?.phase === 'declare') applyRef.current?.({ type: 'END_DECLARE_PHASE' });
    }, declareVoteSeconds * 1000);
    declareVoteBotTimersRef.current.push(timeoutId);
    return () => clearDeclareVoteTimers();
  }, [inDeclare, clearDeclareVoteTimers, declareVoteSeconds, stateRef, applyRef]);

  useEffect(() => {
    if (!inDeclare) return;
    const allReady = [0, 1, 2, 3].every((seat) => Boolean(declareVoteReadyBySeat[seat]));
    if (!allReady || declareVoteAutoSubmittedRef.current) return;
    declareVoteAutoSubmittedRef.current = true;
    if (stateRef.current?.phase === 'declare') applyRef.current?.({ type: 'END_DECLARE_PHASE' });
  }, [inDeclare, declareVoteReadyBySeat, stateRef, applyRef]);

  const declareVoteSecondsLeft = useMemo(() => {
    if (!inDeclare || !declareVoteDeadlineAt) return declareVoteSeconds;
    const ms = Math.max(0, declareVoteDeadlineAt - declareVoteNowMs);
    return Math.ceil(ms / 1000);
  }, [inDeclare, declareVoteDeadlineAt, declareVoteNowMs, declareVoteSeconds]);

  const onAcceptDeclareVote = useCallback(() => {
    setDeclareVoteReadyBySeat((prev) => ({ ...prev, [humanSeat]: true }));
  }, [humanSeat]);

  const preGameTransitionLocked = declareVoteModalBlocked || Date.now() < declareVoteBlockedUntilMs;
  const preGameStage = useMemo(() => {
    if (auctionRevealFx) return 'auction_reveal';
    if (engineState.phase === 'declare') return 'declare_vote';
    if (preGameTransitionLocked) return 'settling';
    return 'idle';
  }, [auctionRevealFx, engineState.phase, preGameTransitionLocked]);

  return {
    auctionRevealFx,
    auctionRevealFallbackBanner,
    declareVoteReadyBySeat,
    declareVoteModalBlocked,
    declareVoteSecondsLeft,
    onAcceptDeclareVote,
    handleAuctionEndedReveal,
    preGameTransitionLocked,
    preGameStage,
  };
}

